import React from 'react';
import logger from '../../utils/logger';
import { Button } from '@chakra-ui/react';
import { RotateCw, AlertTriangle } from 'lucide-react';
import * as Sentry from '@sentry/react';
import errorReportingService from '../../services/errorReportingService';

/**
 * Enhanced Error Boundary Component for MVP
 * Provides graceful error handling with professional UI
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.handleRetry();
    }
  }

  logErrorToService = (error, errorInfo) => {
    const route = this.props.resetKey || window.location.pathname;
    const release =
      this.props.release ||
      process.env.REACT_APP_GIT_SHA ||
      process.env.REACT_APP_COMMIT_SHA ||
      'unknown';

    // Report to Sentry with component stack context
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo?.componentStack,
      },
      tags: {
        source: 'error_boundary',
        category: 'react_error',
        route,
        release,
      },
    });

    // Report to backend monitoring system
    errorReportingService.reportRenderError(error, errorInfo, {
      route,
      release,
    });

    logger.error('Error Boundary caught an error:', error, errorInfo);
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-neutral-900">
          <div className="w-full max-w-lg">
            <div className="rounded-xl bg-white p-8 text-center shadow-lg dark:bg-neutral-800">
              {/* Error Icon */}
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>

              {/* Error Title */}
              <h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
                Algo salió mal
              </h2>

              {/* Error Message */}
              <p className="mb-6 text-gray-600 dark:text-gray-300">
                Ha ocurrido un error inesperado. Por favor, intenta de nuevo o
                recarga la página.
              </p>

              {/* Error Details - Only show in development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mb-6 text-left">
                  <summary className="mb-2 cursor-pointer text-sm text-gray-500 dark:text-gray-400">
                    Ver detalles del error
                  </summary>
                  <div className="max-h-48 overflow-auto rounded-lg bg-gray-100 p-4 font-mono text-xs text-red-600 dark:bg-neutral-700 dark:text-red-400">
                    <div className="mb-2">
                      <strong>Error:</strong> {this.state.error.toString()}
                    </div>
                    <div>
                      <strong>Stack trace:</strong>
                      <pre className="mt-1 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  </div>
                </details>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col justify-center gap-3 sm:flex-row">
                <Button
                  onClick={this.handleRetry}
                  leftIcon={<RotateCw className="h-4 w-4" />}
                  colorScheme="blue"
                  variant="solid"
                  size="md"
                  className="flex-1 sm:flex-initial"
                >
                  Reintentar
                </Button>

                <Button
                  onClick={this.handleReload}
                  variant="outline"
                  size="md"
                  className="flex-1 sm:flex-initial"
                >
                  Recargar página
                </Button>
              </div>

              {/* Help Text */}
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Si el problema persiste, por favor contacta a soporte técnico.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based Error Boundary for functional components
 */
export function useErrorHandler() {
  return (error, errorInfo) => {
    logger.error('Error caught by error handler:', error, errorInfo);

    // You can also trigger a notification here
    if (window.showToast) {
      window.showToast({
        type: 'error',
        message: 'Ha ocurrido un error. Por favor, recarga la página.',
        duration: 7000,
      });
    }
  };
}

/**
 * Simple Error Fallback Component for Suspense
 */
export function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="flex min-h-[200px] flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-6 dark:border-neutral-600 dark:bg-neutral-800">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
        <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
      </div>

      <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
        Error de carga
      </h3>

      <p className="mb-4 max-w-md text-center text-gray-600 dark:text-gray-300">
        No se pudo cargar este componente. Por favor, intenta de nuevo.
      </p>

      <Button
        onClick={resetErrorBoundary}
        leftIcon={<RotateCw className="h-4 w-4" />}
        colorScheme="blue"
        size="sm"
      >
        Reintentar
      </Button>
    </div>
  );
}

/**
 * Network Error Component
 */
export function NetworkError({ onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900">
        <svg
          className="h-8 w-8 text-yellow-600 dark:text-yellow-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>

      <h3 className="mb-3 text-xl font-semibold text-gray-900 dark:text-white">
        Error de conexión
      </h3>

      <p className="mb-6 max-w-md text-gray-600 dark:text-gray-300">
        No se pudo conectar con el servidor. Verifica tu conexión a internet e
        intenta de nuevo.
      </p>

      <Button
        onClick={onRetry}
        leftIcon={<RotateCw className="h-4 w-4" />}
        colorScheme="blue"
        variant="solid"
      >
        Reintentar conexión
      </Button>
    </div>
  );
}

/**
 * 404 Error Component
 */
export function NotFound() {
  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
      <div className="mb-4 text-6xl font-bold text-gray-300 dark:text-gray-600">
        404
      </div>

      <h2 className="mb-3 text-2xl font-semibold text-gray-900 dark:text-white">
        Página no encontrada
      </h2>

      <p className="mb-6 max-w-md text-gray-600 dark:text-gray-300">
        La página que buscas no existe o ha sido movida a otra ubicación.
      </p>

      <Button
        onClick={() => window.history.back()}
        colorScheme="blue"
        variant="outline"
      >
        Volver atrás
      </Button>
    </div>
  );
}
